import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  Snackbar,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Paper,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { API_URL } from '../config';
import { authedFetch } from '../utils/apiUtils';
import { ConfirmDialog } from './common/ConfirmDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MapIcon from '@mui/icons-material/Map';
import AddIcon from '@mui/icons-material/Add';
import StadiumIcon from '@mui/icons-material/Stadium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TerrainIcon from '@mui/icons-material/Terrain';
import LandscapeIcon from '@mui/icons-material/Landscape';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const TRACK_TYPES = ['Dirt', 'Asphalt', 'Clay'] as const;
type TrackType = typeof TRACK_TYPES[number];

const SURFACE_TONE: Record<TrackType, 'warning' | 'info' | 'error'> = {
  Dirt: 'warning',
  Asphalt: 'info',
  Clay: 'error',
};

interface Track {
  track_id: number;
  track_name: string;
  track_city: string;
  track_state: string;
  track_length: number | string;
  track_type: TrackType;
  is_active: boolean;
}

interface TrackRow extends Track {
  location: string;
}

interface FormData {
  track_name: string;
  track_city: string;
  track_state: string;
  track_length: string;
  track_type: TrackType | '';
  is_active: boolean;
}

const emptyFormData: FormData = {
  track_name: '',
  track_city: '',
  track_state: '',
  track_length: '',
  track_type: '',
  is_active: true,
};

function TrackManagement(): React.ReactElement {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trackToDelete, setTrackToDelete] = useState<Track | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterState, setFilterState] = useState<string>('all');
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyFormData);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await authedFetch(`${API_URL}/tracks`);
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      setTracks(data);
    } catch {
      setError('Failed to fetch tracks');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalTracks = tracks.length;
    const activeTracks = tracks.filter((t) => t.is_active).length;
    const byType = tracks.reduce<Record<string, number>>((acc, t) => {
      acc[t.track_type] = (acc[t.track_type] || 0) + 1;
      return acc;
    }, {});
    return [
      { label: 'Total Tracks', value: totalTracks.toLocaleString(), icon: <StadiumIcon /> },
      { label: 'Active', value: activeTracks.toLocaleString(), icon: <CheckCircleIcon />, tone: 'success' as const },
      { label: 'Dirt', value: (byType.Dirt || 0).toLocaleString(), icon: <TerrainIcon /> },
      { label: 'Asphalt', value: (byType.Asphalt || 0).toLocaleString(), icon: <LandscapeIcon /> },
    ];
  }, [tracks]);

  const filteredTracks: TrackRow[] = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return tracks
      .filter((track) => {
        const matchesSearch =
          !q ||
          (track.track_name ?? '').toLowerCase().includes(q) ||
          (track.track_city ?? '').toLowerCase().includes(q) ||
          (track.track_state ?? '').toLowerCase().includes(q);
        const matchesType = filterType === 'all' || track.track_type === filterType;
        const matchesState = filterState === 'all' || track.track_state === filterState;
        return matchesSearch && matchesType && matchesState;
      })
      .map((track) => ({
        ...track,
        location: [track.track_city, track.track_state].filter(Boolean).join(', '),
      }));
  }, [tracks, searchQuery, filterType, filterState]);

  const handleToggleActive = async (track: Track, newStatus: boolean): Promise<void> => {
    try {
      const response = await authedFetch(`${API_URL}/tracks/${track.track_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          track_name: track.track_name,
          track_city: track.track_city,
          track_state: track.track_state,
          track_length: track.track_length,
          track_type: track.track_type,
          is_active: newStatus,
        }),
      });
      if (!response.ok) throw new Error('Failed to update track status');
      setTracks((prev) =>
        prev.map((t) => (t.track_id === track.track_id ? { ...t, is_active: newStatus } : t)),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleEdit = (track: Track): void => {
    setError(null);
    setSelectedTrack(track);
    setFormData({
      track_name: track.track_name,
      track_city: track.track_city,
      track_state: track.track_state,
      track_length: String(track.track_length ?? ''),
      track_type: track.track_type,
      is_active: track.is_active,
    });
    setOpenDialog(true);
  };

  const handleAddClick = (): void => {
    setError(null);
    setSelectedTrack(null);
    setFormData(emptyFormData);
    setOpenDialog(true);
  };

  const requestDelete = (track: Track): void => {
    setTrackToDelete(track);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!trackToDelete) return;
    setIsDeleting(true);
    try {
      const response = await authedFetch(`${API_URL}/tracks/${trackToDelete.track_id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete track');
      }
      setTracks((prev) => prev.filter((t) => t.track_id !== trackToDelete.track_id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setTrackToDelete(null);
    }
  };

  const handleViewMap = (track: Track): void => {
    const query = `${track.track_name} ${track.track_city}, ${track.track_state}`;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank');
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      const url = selectedTrack
        ? `${API_URL}/tracks/${selectedTrack.track_id}`
        : `${API_URL}/tracks`;
      const length = formData.track_length ? parseFloat(formData.track_length).toFixed(3) : null;
      const response = await authedFetch(url, {
        method: selectedTrack ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, track_length: length }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save track');
      }
      setOpenDialog(false);
      setSelectedTrack(null);
      setFormData(emptyFormData);
      fetchTracks();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleExport = (): void => {
    const escapeCell = (value: unknown): string => {
      const str = String(value ?? '');
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const csvContent = [
      ['Track Name', 'City', 'State', 'Length (mi)', 'Surface Type', 'Status'],
      ...tracks.map((t) => [
        t.track_name,
        t.track_city,
        t.track_state,
        t.track_length,
        t.track_type,
        t.is_active ? 'Active' : 'Inactive',
      ]),
    ]
      .map((row) => row.map(escapeCell).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.setAttribute('href', objectUrl);
    link.setAttribute('download', `tracks_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const columns: GridColDef[] = [
    {
      field: 'track_name',
      headerName: 'Track',
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams) => (
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          {params.value as string}
        </Typography>
      ),
    },
    { field: 'location', headerName: 'Location', width: 200 },
    {
      field: 'track_length',
      headerName: 'Length',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const num = parseFloat(params.value as string);
        if (isNaN(num)) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {num.toFixed(3)} mi
          </Typography>
        );
      },
    },
    {
      field: 'track_type',
      headerName: 'Surface',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as TrackType;
        return <Chip label={value} color={SURFACE_TONE[value] ?? 'default'} size="small" variant="outlined" />;
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
          onClick={() => handleToggleActive(params.row as Track, !params.value)}
          sx={{ cursor: 'pointer' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View on map">
            <IconButton
              size="small"
              onClick={() => handleViewMap(params.row as Track)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
              }}
            >
              <MapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row as Track)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => requestDelete(params.row as Track)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'error.main' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          Tracks
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
            Add Track
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box
                    sx={{
                      color: stat.tone === 'success' ? 'success.main' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            placeholder="Search tracks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Surface</InputLabel>
            <Select
              value={filterType}
              onChange={(e: SelectChangeEvent) => setFilterType(e.target.value)}
              label="Surface"
            >
              <MenuItem value="all">All surfaces</MenuItem>
              {TRACK_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>State</InputLabel>
            <Select
              value={filterState}
              onChange={(e: SelectChangeEvent) => setFilterState(e.target.value)}
              label="State"
            >
              <MenuItem value="all">All states</MenuItem>
              {US_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={filteredTracks}
          columns={columns}
          getRowId={(row) => row.track_id}
          loading={loading}
          autoHeight
          disableSelectionOnClick
          sx={{
            border: 'none',
            fontFamily: 'inherit',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
              borderBottom: '1px solid',
              borderColor: 'divider',
              minHeight: '40px !important',
              maxHeight: '40px !important',
              lineHeight: '40px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '0 12px',
              '&:focus, &:focus-within': { outline: 'none' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
            },
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
            '& .MuiDataGrid-cell': {
              padding: '0 12px',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderBottom: '1px solid',
              borderColor: 'grey.100',
              '&:focus, &:focus-within': { outline: 'none' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
            },
            '& .MuiDataGrid-row': {
              transition: 'background-color 120ms',
              '&:hover': { backgroundColor: 'action.hover' },
            },
            '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: '44px',
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.8125rem',
            },
          }}
        />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTrack ? 'Edit Track' : 'Add Track'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Track Name"
                value={formData.track_name}
                onChange={(e) => setFormData({ ...formData, track_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="City"
                value={formData.track_city}
                onChange={(e) => setFormData({ ...formData, track_city: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.track_state}
                  label="State"
                  onChange={(e) => setFormData({ ...formData, track_state: e.target.value })}
                >
                  {US_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Length (miles)"
                type="number"
                value={formData.track_length}
                onChange={(e) => setFormData({ ...formData, track_length: e.target.value })}
                inputProps={{ step: 0.001 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Surface</InputLabel>
                <Select
                  value={formData.track_type}
                  label="Surface"
                  onChange={(e) =>
                    setFormData({ ...formData, track_type: e.target.value as TrackType })
                  }
                >
                  {TRACK_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedTrack ? 'Save changes' : 'Add track'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Track"
        message={
          trackToDelete
            ? `Delete ${trackToDelete.track_name}? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        loading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setTrackToDelete(null);
        }}
      />

      <Snackbar
        open={error !== null && !openDialog && !deleteDialogOpen}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TrackManagement;
